<template>
	<div class="kpis_tab report_theme_container" data-theme="ventas">
		<TabHeader
			title="KPIs"
			description="Indicadores de actividad de clientes"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendarMode="range"
			:dateRange="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange" />

		<!-- Grilla 2×2 de KPIs. Hoy el primer card es la actividad de clientes
			(cantidad + ticket promedio); los otros tres quedan reservados para
			próximos indicadores. -->
		<div class="kpi_grid mt-2">
			<article class="kpi_card">
				<header class="kpi_card_header">
					<span class="kpi_card_title">Cantidad de clientes y ticket promedio</span>
				</header>
				<div class="kpi_card_body">
					<p v-if="error" class="kpi_state kpi_state--error">{{ error }}</p>
					<p v-else-if="isLoading" class="kpi_state">Cargando…</p>
					<p v-else-if="!hasActivityData" class="kpi_state">
						Sin datos de clientes para el período seleccionado.
					</p>
					<SalesClientsActivityChart v-else
						:monthHeaders="chartsData.monthHeaders"
						:clientCounts="chartsData.clientCounts"
						:avgTickets="chartsData.avgTickets" />
				</div>
			</article>

			<article v-for="n in 3" :key="n" class="kpi_card kpi_card--placeholder">
				<div class="kpi_state">Próximamente</div>
			</article>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapActions } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import SalesClientsActivityChart from "@/components/financialReports/charts/SalesClientsActivityChart.vue";
import {
	FILTER_PERIOD_OPTIONS,
	TOP_CLIENTS_COUNT,
} from "@/config/reportStructure";
import { defaultYearRange } from "@/store/financialReports/utils/dateRange";

export default {
	name: "KpisTab",

	components: {
		TabHeader,
		SalesClientsActivityChart,
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			// Filtro de tiempo PROPIO del tab — independiente del store global
			// `reportsFilters` (que comparten los demás tabs), así sus consultas
			// no interfieren entre sí.
			filterPeriod: "month",
			endMonth: moment().toDate(),
			selectedPeriodRange: "lastYear",
			customRange: null,
			// Rango de años (granularidad Anual). Local como el resto de los
			// filtros de este tab — no comparte el reportsFilters global.
			customYearRange: null,
		};
	},

	computed: {
		...mapState("kpisReport", ["chartsData", "isLoading", "error"]),

		getNow() {
			return moment().toDate();
		},

		// Rango activo del calendar del header. Si hay `customRange` lo usamos
		// tal cual; si no, derivamos el último año a partir de endMonth para que
		// el calendar abra pintando un rango razonable en lugar de "—".
		calendarRange() {
			if (this.customRange) return this.customRange;
			const end = moment(this.endMonth).startOf("month");
			return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
		},

		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
		// abra pintando el span real y nunca difiera de la tabla.
		yearRange() {
			return this.customYearRange || defaultYearRange(this.endMonth);
		},

		hasActivityData() {
			return Boolean(
				this.chartsData
				&& this.chartsData.monthHeaders.length > 0
				&& this.chartsData.clientCounts.length > 0,
			);
		},

		// Firma reactiva: un solo trigger de fetch aunque cambien varios filtros
		// en la misma transacción.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.filterPeriod,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},

	methods: {
		...mapActions("kpisReport", ["fetchKpisCharts"]),

		loadCharts() {
			this.fetchKpisCharts({
				endMonth: this.endMonth,
				selectedPeriodRange: this.selectedPeriodRange,
				customRange: this.customRange,
				customYearRange: this.customYearRange,
				period: this.filterPeriod,
				topN: TOP_CLIENTS_COUNT,
			});
		},
		updateEndMonth(date) {
			this.endMonth = date;
		},
		updateDateRange(range) {
			if (!range || !range.from || !range.to) return;
			this.customRange = range;
			this.selectedPeriodRange = "custom";
			this.endMonth = range.to;
		},
		// Rango de años (Anual). Ortogonal al mes: no toca endMonth ni
		// selectedPeriodRange.
		updateYearRange(range) {
			if (!range || !range.from || !range.to) return;
			this.customYearRange = range;
		},
		updateFilterPeriod(period) {
			this.filterPeriod = period;
		},
	},

	mounted() {
		this.loadCharts();
	},

	watch: {
		fetchSignature() {
			this.loadCharts();
		},
	},
};
</script>

<style scoped lang="scss">
.kpis_tab {
	position: relative;
	height: 100%;
}

.kpi_grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-auto-rows: 340px;
	gap: 16px;
	width: 100%;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
}

.kpi_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.kpi_card_header {
	flex-shrink: 0;
	margin-bottom: 8px;
}

.kpi_card_title {
	color: var(--report-text);
	font-size: 14px;
	font-weight: 600;
}

.kpi_card_body {
	flex: 1;
	min-height: 0;
	position: relative;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
}

.kpi_card--placeholder {
	align-items: center;
	justify-content: center;
	border-style: dashed;
}

.kpi_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;

	&--error {
		color: var(--report-negative);
	}
}
</style>
